import { useParams } from 'react-router-dom';
import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const PropertyDetails = () => {
  const { id, action } = useParams();
  const [promoCode, setPromoCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [paymentOption, setPaymentOption] = useState('');
  const [showVoucher, setShowVoucher] = useState(false);

  const property = {
    id: 1,
    title: "Luxury Beachfront Villa",
    location: "Goa, India",
    description: "Experience tranquility in a modern beachfront villa with private pool and scenic views.",
    originalPrice: 15000,
    offerPrice: 12000,
    agent: {
      name: "Sundaze Properties",
      type: "Real Estate Agency",
      contact: "bookings@sundaze.com",
      location: "Candolim, Goa"
    },
    image: `/api/downloads/thinkproperty/property/${id}.jpg`,
    reviews: 45
  };

  const finalPrice = discount > 0 ? property.offerPrice * (1 - discount / 100) : property.offerPrice;

  const handleVerify = () => {
    if (promoCode === 'HOME10') {
      setIsVerified(true);
      setDiscount(10);
    } else {
      setIsVerified(false);
      setDiscount(0);
    }
  };

  const handleBooking = () => {
    if (paymentOption) {
      setShowVoucher(true);
    } else {
      alert('Please select a payment option before proceeding.');
    }
  };

  const handleDownloadPDF = async () => {
    const input = document.getElementById('voucherSection');
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${property?.title || "property-info"}.pdf`);
  };

  const renderPromoSection = () => (
    <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-2">
      <input
        type="text"
        value={promoCode}
        onChange={(e) => setPromoCode(e.target.value)}
        placeholder="Enter Promo Code"
        className="border p-2 rounded w-full sm:w-64"
      />
      <button
        onClick={handleVerify}
        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 w-full sm:w-40"
      >
        Verify Code
      </button>
      {promoCode && !isVerified && <p className="text-red-300 mt-2">Invalid promo code.</p>}
      {isVerified && (
        <button
          onClick={() => alert("Promo Applied")}
          className="mt-2 sm:mt-0 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Apply Code
        </button>
      )}
    </div>
  );

  const renderPaymentOptions = () => (
    <div className="mt-6">
      <h4 className="font-semibold mb-2 text-white">Select Payment Option:</h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {["Online Payment", "Already Paid", "Pay at Property"].map(opt => (
          <label key={opt} className="flex items-center gap-2">
            <input
              type="radio"
              name="payment"
              value={opt}
              onChange={(e) => setPaymentOption(e.target.value)}
              className="accent-green-600"
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
      {paymentOption && (
        <p className="mt-2 text-yellow-200">You selected: {paymentOption}</p>
      )}
      <button
        className="mt-4 px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-full"
        onClick={handleBooking}
        disabled={!paymentOption}
      >
        Proceed to Book
      </button>
    </div>
  );

  const renderVoucher = () => showVoucher && (
    <>
      <div className="mt-8 bg-white shadow-lg rounded-lg p-4 text-black" id="voucherSection">
        <img
          src={property?.image || "/default-property.jpg"}
          alt="Property"
          className="w-full h-64 object-cover rounded-lg mb-4"
        />

        <h2 className="text-2xl font-bold">{property?.title}</h2>
        <h3 className="text-lg text-gray-800 mb-2">{property?.location}</h3>
        <p className="text-gray-900 mb-4">{property?.description}</p>

        <div className="text-base text-gray-800 space-y-1 mb-4">
          <p><strong>Original Price:</strong> ₹{property.originalPrice}</p>
          <p><strong>Offer Price:</strong> ₹{property.offerPrice}</p>
          <p><strong>Final Price:</strong> ₹{finalPrice}</p>
        </div>

        <h4 className="text-lg font-semibold mt-4 mb-2">Agent Details</h4>
        <div className="text-sm text-gray-800 space-y-1">
          <p><strong>{property.agent.name}</strong> ({property.agent.type})</p>
          <p>{property.agent.location}</p>
          <p>{property.agent.contact}</p>
        </div>
      </div>

      <button
        onClick={handleDownloadPDF}
        className="mt-6 px-6 py-2 bg-green-600 text-white rounded-full hover:bg-green-700"
      >
        Download Booking Voucher
      </button>
    </>
  );

  return (
    <div className="text-white p-6 max-w-4xl mx-auto bg-blue-500 rounded-lg">
      <img src={property.image} alt={property.title} className="w-full h-64 object-cover rounded mb-4" />

      <h2 className="text-3xl font-bold text-yellow-200">{property.title}</h2>
      <p className="text-white">{property.location}</p>

      <div className="mt-4">
        <p>{property.description}</p>
        <p className="mt-2">Original Price: ₹{property.originalPrice}</p>
        <p>Offer Price: ₹{property.offerPrice}</p>
        {discount > 0 && (
          <p className="text-green-300 font-bold">Discount Applied: ₹{property.offerPrice - finalPrice}</p>
        )}
        <p className="text-xl mt-2 font-semibold">Final Price: ₹{finalPrice}</p>
      </div>

      <div className="mt-4">
        <h3 className="font-semibold">Agent/Owner Details</h3>
        <p>{property.agent.name} ({property.agent.type})</p>
        <p>{property.agent.location}</p>
        <p>{property.agent.contact}</p>
      </div>

      {/* Section render based on `action` param */}
      {action === 'ask-ai' && <div className="mt-6">🔮 <strong>Ask AI coming soon...</strong></div>}
      {action === 'contact-seller' && <div className="mt-6">📨 <strong>Seller Contact Form here...</strong></div>}
      {action === 'promo' && renderPromoSection()}
      {action === 'payment' && renderPaymentOptions()}
      {action === 'voucher' && renderVoucher()}
    </div>
  );
};

export default PropertyDetails;
